import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { lazy, Suspense, useEffect, useMemo } from 'react';
import { useGetDocument } from '../../hooks/useGetDocument';
import { useChatArtifactStore } from '../../lib/stores/ChatArtifactStore';
import { useToolEvents } from '../../lib/tool-stream';
// Lazy load DocumentInlinePreview to avoid loading Handsontable bundle on every chat message
const DocumentInlinePreview = /*#__PURE__*/ lazy(()=>import('./document-inline-preview').then((mod)=>({
            default: mod.DocumentInlinePreview
        })));
/**
 * Check if the document is currently being streamed/created
 */ function isDocumentStreaming(progressCallId, events) {
    return Boolean(progressCallId && (events.length === 0 || !/saved/i.test(String(events[events.length - 1]?.message || ''))));
}
/**
 * Extract the heading from content as the display title
 */ function extractHeading(content, fallbackTitle) {
    const lines = content.split(/\n/);
    const h1 = lines.find((l)=>/^#\s+/.test(l));
    return h1 ? h1.replace(/^#\s+/, '') : fallbackTitle;
}
/**
 * Inline preview for artifacts with streaming support
 * NOTE: Not memoized - content prop changes during streaming
 *
 * Lazy-loads content when not provided in initial artifact emission
 */ export function ArtifactPreviewInline(props) {
    const { id, title, kind, content, progressCallId, sourceAttachmentId, sourceUrl, contentType } = props;
    const updateArtifact = useChatArtifactStore((state)=>state.updateArtifact);
    // Fetch document data when content is empty (lazy-loaded artifacts)
    const { data: documentData } = useGetDocument(!content ? id : null);
    // Update artifact store with fetched content
    useEffect(()=>{
        if (documentData?.latest && id && !content) {
            updateArtifact(id, {
                title: documentData.latest.title,
                content: documentData.latest.content,
                updatedAt: documentData.latest.updatedAt
            });
        }
    }, [
        documentData,
        id,
        content,
        updateArtifact
    ]);
    const { events } = useToolEvents((ev)=>Boolean(progressCallId && ev.callId === progressCallId && ev.type.startsWith('data-createDocument-')));
    const isStreaming = isDocumentStreaming(progressCallId, events);
    // Use fetched content if available, otherwise use prop content
    const displayContent = documentData?.latest?.content || content;
    const displayTitle = useMemo(()=>extractHeading(displayContent, title), [
        displayContent,
        title
    ]);
    return /*#__PURE__*/ _jsx(Suspense, {
        fallback: /*#__PURE__*/ _jsx("div", {
            className: "w-full p-4 border rounded-lg bg-muted/30",
            children: /*#__PURE__*/ _jsxs("div", {
                className: "animate-pulse space-y-2",
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: "h-4 bg-muted rounded w-3/4"
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "h-4 bg-muted rounded w-1/2"
                    })
                ]
            })
        }),
        children: /*#__PURE__*/ _jsx(DocumentInlinePreview, {
            id: id,
            title: displayTitle,
            kind: kind,
            content: displayContent,
            isStreaming: isStreaming,
            sourceAttachmentId: sourceAttachmentId,
            sourceUrl: sourceUrl,
            contentType: contentType
        })
    });
}
